import { createFileRoute, notFound } from "@tanstack/react-router";
import { getCalculatorBySlug } from "@/lib/calculators-catalog";
import { CALCULATOR_CONTENT } from "@/lib/calculator-content";
import { CalculatorShell } from "@/components/calculators/CalculatorShell";
import { CalculatorRenderer } from "@/components/calculators/CalculatorRenderer";
import { getEsCalculatorSlugForEnSlug } from "@/lib/es-calculators-catalog";
import { getDeCalculatorSlugForEnSlug } from "@/lib/de-calculators-catalog";
import { getFrCalculatorSlugForEnSlug } from "@/lib/fr-calculators-catalog";
import { getPtBrCalculatorSlugForEnSlug } from "@/lib/pt-br-calculators-catalog";

export const Route = createFileRoute("/calculators/$slug")({
  loader: ({ params }) => {
    const calc = getCalculatorBySlug(params.slug);
    if (!calc) throw notFound();
    const content = CALCULATOR_CONTENT[calc.slug];
    if (!content) throw notFound();
    return { calc, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { calc, content } = loaderData;
    const currentCanonical = `https://picamini.online/calculators/${calc.slug}`;
    const esUrl = `https://picamini.online/es/calculators/${getEsCalculatorSlugForEnSlug(calc.slug)}`;
    const deUrl = `https://picamini.online/de/calculators/${getDeCalculatorSlugForEnSlug(calc.slug)}`;
    const frUrl = `https://picamini.online/fr/calculators/${getFrCalculatorSlugForEnSlug(calc.slug)}`;
    const ptUrl = `https://picamini.online/pt-br/calculators/${getPtBrCalculatorSlugForEnSlug(calc.slug)}`;

    return {
      meta: [
        { title: `${content.h1Title || calc.name} | Picamini.online` },
        { name: "description", content: content.seoDescription },
        { name: "keywords", content: calc.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.h1Title || calc.name },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: currentCanonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${calc.name} - Free Online Calculator on Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.h1Title || calc.name },
        { name: "twitter:description", content: content.seoDescription },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      ],
      links: [
        { rel: "canonical", href: currentCanonical },
        { rel: "alternate", hrefLang: "en", href: currentCanonical },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "fr", href: frUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "x-default", href: currentCanonical },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": content.h1Title || calc.name,
            "url": currentCanonical,
            "description": content.seoDescription,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "inLanguage": "en",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
            },
            "featureList": content.features?.map((f: { title: string }) => f.title) || [],
            "datePublished": "2024-01-15",
            "dateModified": "2026-09-15",
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": ["h1", "article p:first-of-type", ".calculator-description"]
            }
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://picamini.online/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Calculators",
                "item": "https://picamini.online/calculators",
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": content.h1Title || calc.name,
                "item": currentCanonical,
              },
            ],
          }),
        },
        ...(content.faq && content.faq.length > 0
          ? [{
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": content.faq.map((item: { q: string; a: string }) => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a,
                  },
                })),
              }),
            }]
          : []),
      ],
    };
  },
  component: SingleCalculatorPage,
});


function SingleCalculatorPage() {
  const { calc, content } = Route.useLoaderData();

  return (
    <CalculatorShell calc={calc} content={content} lang="en">
      <CalculatorRenderer slug={calc.slug} />
    </CalculatorShell>
  );
}
